import { useEffect, useState } from 'react';

import MainProtected from '@/api/main-protected';
import { ExpenseAttachment } from '@/store/reducers/payout';

// Mirrors be/src/utils/expenseAttachmentFiles.ts. The server sniffs the bytes and
// has the final word; this only catches a wrong pick before it is uploaded.
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_FILES_PER_UPLOAD = 10;
export const ATTACHMENT_ACCEPT = 'image/*,application/pdf';

export const isImage = (mimeType: string) => mimeType.startsWith('image/');

// Some browsers hand over a HEIC photo with an empty type, so the name counts too.
export const checkFile = (file: File): 'type' | 'size' | undefined => {
  const byType = file.type.startsWith('image/') || file.type === 'application/pdf';
  const byName = /\.(pdf|heic|heif)$/i.test(file.name);
  if (!byType && !byName) return 'type';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'size';

  return undefined;
};

export const formatSize = (bytes: number) => (
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
);

// One download per attachment per page load: a thumbnail and the viewer share it.
// A failed load is forgotten so the next look retries.
const urls = new Map<number, Promise<string>>();

export const attachmentUrl = (attachment: ExpenseAttachment) => {
  const cached = urls.get(attachment._id);
  if (cached) return cached;

  const loading = MainProtected.getInstance()
    .getExpenseAttachment(attachment._id)
    // Re-typed from what the server stored, which is only ever an image or a PDF --
    // never anything a blob: URL on this origin would run.
    .then((blob) => URL.createObjectURL(new Blob([blob], { type: attachment.mimeType })));
  loading.catch(() => urls.delete(attachment._id));
  urls.set(attachment._id, loading);

  return loading;
};

export const forgetAttachment = (id: number) => {
  const cached = urls.get(id);
  urls.delete(id);
  if (cached) cached.then(URL.revokeObjectURL).catch(() => undefined);
};

export const useAttachmentUrl = (attachment: ExpenseAttachment) => {
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    attachmentUrl(attachment)
      .then((loaded) => { if (alive) setUrl(loaded); })
      .catch(() => { if (alive) setFailed(true); });

    return () => { alive = false; };
  }, [attachment._id]);

  return { url, failed };
};

// A PDF opens in the browser's own viewer; phones do not render one inline.
export const openInNewTab = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.click();
};

export const download = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
};
